import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

const CART_SYNC_EVENT = 'herbslab:cart-sync';
const CART_REQUEST_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;

function requestCartSync() {
  document.dispatchEvent(new CustomEvent(CART_SYNC_EVENT));
}

function installCartRequestWatcher() {
  if (window.__herbslabCartRequestWatcherInstalled) return;

  window.__herbslabCartRequestWatcherInstalled = true;

  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const request = args[0];

    const url =
      typeof request === 'string'
        ? request
        : request instanceof Request
          ? request.url
          : '';

    const response = await originalFetch.apply(this, args);

    if (CART_REQUEST_PATTERN.test(url)) {
      window.clearTimeout(window.__herbslabCartSyncTimer);

      window.__herbslabCartSyncTimer = window.setTimeout(() => {
        requestCartSync();
      }, 250);
    }

    return response;
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__herbslabCartRequestUrl =
      typeof url === 'string' ? url : String(url ?? '');

    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (CART_REQUEST_PATTERN.test(this.__herbslabCartRequestUrl || '')) {
      this.addEventListener(
        'loadend',
        () => {
          window.clearTimeout(window.__herbslabCartSyncTimer);

          window.__herbslabCartSyncTimer = window.setTimeout(() => {
            requestCartSync();
          }, 250);
        },
        { once: true }
      );
    }

    return originalXhrSend.apply(this, args);
  };
}

installCartRequestWatcher();

/**
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  syncTimer = null;
  syncRequestId = 0;

  /** @type {number} */
  get currentCartCount() {
    const count = Number.parseInt(
      this.refs.cartBubbleCount?.textContent || '0',
      10
    );

    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    if (!this.refs.cartBubbleCount) return;

    const normalizedValue = Number.isFinite(value)
      ? Math.max(0, value)
      : 0;

    this.refs.cartBubbleCount.textContent =
      normalizedValue < 100 ? String(normalizedValue) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.onThemeCartUpdate
    );

    document.addEventListener(
      CART_SYNC_EVENT,
      this.onExternalCartUpdate
    );

    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener(
      'visibilitychange',
      this.onVisibilityChange
    );

    this.scheduleCartSync(0, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.onThemeCartUpdate
    );

    document.removeEventListener(
      CART_SYNC_EVENT,
      this.onExternalCartUpdate
    );

    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange
    );

    if (this.syncTimer) {
      window.clearTimeout(this.syncTimer);
    }
  }


  onThemeCartUpdate = () => {
    this.scheduleCartSync(100, true);
  };

  onExternalCartUpdate = () => {
    this.scheduleCartSync(100, true);
  };

  onPageShow = () => {
    this.scheduleCartSync(0, false);
  };

  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.scheduleCartSync(0, false);
    }
  };

  /**
   *
   * @param {number} delay
   * @param {boolean} animate
   */
  scheduleCartSync = (delay = 100, animate = true) => {
    if (this.syncTimer) {
      window.clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      this.syncCartFromServer(animate);
    }, delay);
  };

  /**
   *
   * @param {boolean} animate
   */
  syncCartFromServer = async (animate = true) => {
    const requestId = ++this.syncRequestId;

    try {
      const separator = window.Shopify?.routes?.root || '/';

      const cartUrl =
        `${separator}cart.js?timestamp=${Date.now()}`.replace(
          /([^:]\/)\/+/g,
          '$1'
        );

      const response = await fetch(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Cart request failed with status ${response.status}`
        );
      }

      const cart = await response.json();

      if (requestId !== this.syncRequestId) return;

      const itemCount = Number(cart.item_count || 0);

      await this.renderCartBubble(itemCount, animate);
    } catch (error) {
      console.warn('[CartIcon] Failed to sync cart:', error);

      this.restoreCountFromStorage();
    }
  };

  /**
   *
   * @param {number} itemCount
   * @param {boolean} animate
   */
  renderCartBubble = async (itemCount, animate = true) => {
    if (
      !this.refs.cartBubble ||
      !this.refs.cartBubbleText ||
      !this.refs.cartBubbleCount
    ) {
      return;
    }

    const normalizedCount = Number.isFinite(itemCount)
      ? Math.max(0, itemCount)
      : 0;

    const previousCount = this.currentCartCount;
    const hasItems = normalizedCount > 0;

    this.currentCartCount = normalizedCount;

    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      !hasItems
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      !hasItems
    );

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      hasItems
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: normalizedCount,
        timestamp: Date.now(),
      })
    );

    if (
      !animate ||
      !hasItems ||
      previousCount === normalizedCount
    ) {
      return;
    }

    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );

    void this.refs.cartBubble.offsetWidth;

    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    try {
      await onAnimationEnd(this.refs.cartBubbleText);
    } finally {
      this.refs.cartBubble.classList.remove(
        'cart-bubble--animating'
      );
    }
  };

  restoreCountFromStorage = () => {
    const storedValue = sessionStorage.getItem('cart-count');

    if (!storedValue) return;

    try {
      const parsed = JSON.parse(storedValue);
      const count = Number.parseInt(parsed.value, 10);

      if (Number.isFinite(count) && count >= 0) {
        this.renderCartBubble(count, false);
      }
    } catch (_) {
      sessionStorage.removeItem('cart-count');
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}